import { isDefined } from '@sgme/fp';
import type { ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import type { IBlotterItem } from 'models/IBlotterItem';
import { useCallback } from 'react';
import { blotterPanelItemDetailsRequestedThunk } from 'state/blotters/blotterPanel/blotterPanel.thunks';
import {
  tradesBlottersColumnStateChanged,
  tradesBlottersItemsFetchingFailed,
  tradesBlottersItemsPerPageChanged,
  tradesBlottersSetLoading,
} from 'state/blotters/trades/tradesBlotters.actions';
import {
  getTradesBlotterPreferenceState,
  getTradesBlotterRequestByBlotterType,
  selectTradesBlottersColumnDefinition,
} from 'state/blotters/trades/tradesBlotters.selector';
import type { TradesOrProducts, TradesOrProductsByBlotterType } from 'state/blotters/trades/types/blotterItem';
import { getUserPreferencesLanguage } from 'state/selectors';
import { isPastDate } from 'utils/core/date';

export function useTradesBlotterState<T extends TradesOrProducts>(blotterType: T) {
  type TItem = TradesOrProductsByBlotterType<T>;

  // Redux selectors
  const isoLang = useAppSelector(state => getUserPreferencesLanguage(state));
  const blotterRequest = useAppSelector(state => getTradesBlotterRequestByBlotterType(state, blotterType, isoLang));
  const { columnState, columnGroupState, pageSizeState } = useAppSelector(state =>
    getTradesBlotterPreferenceState(state, blotterType),
  );
  const columnDefinitions = useAppSelector(state => selectTradesBlottersColumnDefinition(state, blotterType));

  const isDateFilterCoherent =
    isDefined(blotterRequest.startDate) &&
    isDefined(blotterRequest.endDate) &&
    blotterRequest.startDate < blotterRequest.endDate;

  const dispatch = useThunkDispatch();

  // Redux callbacks
  const onBlotterColumnStateChanged = useCallback(
    (columnState: readonly ColumnPreferenceState[]) => {
      dispatch(tradesBlottersColumnStateChanged(blotterType, columnState));
    },
    [dispatch, blotterType],
  );

  const onSetActiveItem = useCallback(
    (item?: TItem) => {
      dispatch(
        blotterPanelItemDetailsRequestedThunk(
          item => (blotterType === 'products' ? item?.productId : item?.tradeId),
          item as IBlotterItem,
        ),
      );
    },
    [dispatch, blotterType],
  );

  const onItemsReceived = useCallback(() => {
    dispatch(tradesBlottersSetLoading<TradesOrProducts>(blotterType, false, undefined));
  }, [dispatch, blotterType]);

  const onItemsRequested = useCallback(() => {
    dispatch(tradesBlottersSetLoading<TradesOrProducts>(blotterType, true, undefined));
  }, [dispatch, blotterType]);

  const onRequestFailed = useCallback(() => {
    dispatch(tradesBlottersItemsFetchingFailed<TradesOrProducts, unknown>(blotterType, undefined, undefined));
  }, [dispatch, blotterType]);

  const onPageSizeChanged = useCallback(
    (pageSize: number) => {
      dispatch(tradesBlottersItemsPerPageChanged<TradesOrProducts>(blotterType, pageSize));
    },
    [dispatch, blotterType],
  );

  return {
    columnDefinitions,
    blotterRequest,
    isBlotterRefreshable: isDateFilterCoherent && isPastDate(blotterRequest.endDate) !== true,
    onBlotterColumnStateChanged,
    onSetActiveItem,
    onItemsReceived,
    onItemsRequested,
    onRequestFailed,
    onPageSizeChanged,
    columnState,
    columnGroupState,
    pageSizeState,
  };
}
